import React, { useMemo } from "react";

// Default colored orb palette per scene-class (tinted blobs that float around)
const ORBS_BY_SCENE = {
  "scene-forest":    ["#22c55e", "#16a34a", "#86efac"],
  "scene-cavern":    ["#a8a29e", "#78716c", "#d6d3d1"],
  "scene-ruins":     ["#d97706", "#b45309", "#78716c"],
  "scene-glacial":   ["#38bdf8", "#67e8f9", "#bae6fd"],
  "scene-volcano":   ["#f43f5e", "#ea580c", "#fca5a5"],
  "scene-celestial": ["#fde047", "#facc15", "#fef3c7"],
  "scene-astral":    ["#a78bfa", "#38bdf8", "#f472b6"],
  "scene-abyss":     ["#7c3aed", "#1e1b4b", "#a78bfa"],
};

/**
 * Decorative full-screen backdrop with three animated layers:
 *  1) base scene gradient (.scene-*)
 *  2) colored parallax orbs (.scene-orb)
 *  3) drifting emoji particles (.scene-particle)
 *  4) starfield + sweep for life
 */
export const SceneBackdrop = ({ sceneClass, particle, count = 8, overlayClass = "" }) => {
  const particles = useMemo(() => (
    Array.from({ length: count }).map((_, i) => ({
      key: i,
      left: Math.random() * 100,
      delay: Math.random() * 6,
      duration: 6 + Math.random() * 6,
      size: 0.9 + Math.random() * 1.1,
    }))
  ), [count]);

  const orbs = useMemo(() => {
    const palette = ORBS_BY_SCENE[sceneClass] || ["#a78bfa", "#38bdf8", "#f472b6"];
    return palette.map((color, i) => ({
      key: i,
      color,
      size: 320 + Math.random() * 220,
      top: 10 + Math.random() * 70,
      left: 10 + Math.random() * 70,
      cls: ["scene-orb-a", "scene-orb-b", "scene-orb-c"][i % 3],
      delay: Math.random() * 4,
    }));
  }, [sceneClass]);

  return (
    <div className={`absolute inset-0 ${sceneClass} ${overlayClass} bg-dots`} aria-hidden="true">
      {/* Animated colored orbs */}
      {orbs.map((o) => (
        <span
          key={o.key}
          className={`scene-orb ${o.cls}`}
          style={{
            top: `${o.top}%`,
            left: `${o.left}%`,
            width: `${o.size}px`,
            height: `${o.size}px`,
            background: o.color,
            animationDelay: `-${o.delay}s`,
          }}
        />
      ))}
      {/* Twinkling stars for celestial / abyss / astral; harmless for others */}
      <div className="scene-stars" />
      <div className="scene-sweep" />
      {/* Drifting particles */}
      {particles.map((p) => (
        <span
          key={p.key}
          className="scene-particle"
          style={{
            left: `${p.left}%`,
            bottom: "-2rem",
            animationDelay: `-${p.delay}s`,
            animationDuration: `${p.duration}s`,
            fontSize: `${p.size}rem`,
          }}
        >
          {particle}
        </span>
      ))}
    </div>
  );
};
